import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, Grid, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory2Outlined';
import RequestQuoteIcon from '@mui/icons-material/RequestQuoteOutlined';
import TodayIcon from '@mui/icons-material/TodayOutlined';
import DateRangeIcon from '@mui/icons-material/DateRangeOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import EventRepeatIcon from '@mui/icons-material/EventRepeatOutlined';
import BuildIcon from '@mui/icons-material/BuildOutlined';
import WarehouseIcon from '@mui/icons-material/WarehouseOutlined';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import useAuth from '../../auth/useAuth';
import serviceOrdersApi from '../../api/serviceOrdersApi';
import ordersApi from '../../api/ordersApi';
import productsApi from '../../api/productsApi';
import { ROLES } from '../../config';

// Opciones de agrupacion de todos los graficos del dashboard, tal como las exponen
// GET /orders/stats, GET /products/stats y GET /service-orders/stats.
const GRANULARITY_OPTIONS = [
  { value: 'day', label: 'Día' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Año' },
];

const formatMoney = (value) =>
  new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(value || 0);

function MetricCard({ label, value, caption, icon, color }) {
  return (
    <Card>
      <CardContent>
        <Stack sx={{ alignItems: 'center' }} direction="row" spacing={2}>
          <Stack
            sx={{
              alignItems: 'center', justifyContent: 'center', width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: `${color}.lighter`,
              color: `${color}.dark`,
            }}
          >
            {icon}
          </Stack>
          <Stack sx={{ minWidth: 0 }}>
            <Typography variant="h4" noWrap>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {label}
            </Typography>
            {caption && (
              <Typography variant="caption" color="text.secondary">
                {caption}
              </Typography>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Tarjeta con titulo + selector de granularidad (opcional) + grafico, para no repetir
// el mismo encabezado en cada seccion del dashboard.
function ChartCard({ title, description, granularityLabel, children, empty }) {
  return (
    <Card>
      <CardContent>
        <Stack sx={{ mb: 2 }}>
          <Typography variant="h6">{title}</Typography>
          <Typography variant="body2" color="text.secondary">
            {description} {granularityLabel && `agrupado por ${granularityLabel}.`}
          </Typography>
        </Stack>

        <ResponsiveContainer width="100%" height={280}>
          {children}
        </ResponsiveContainer>

        {empty && (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
            Aún no hay datos registrados en este período.
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardHomePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.ADMINISTRADOR;

  const [activeServiceOrders, setActiveServiceOrders] = useState(null);
  const [granularity, setGranularity] = useState('day');

  const [salesStats, setSalesStats] = useState(null);
  const [productStats, setProductStats] = useState(null);
  const [serviceStats, setServiceStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(isAdmin);

  const granularityLabel = GRANULARITY_OPTIONS.find((o) => o.value === granularity)?.label.toLowerCase();

  // Cuenta las ordenes de servicio que aun no fueron entregadas al cliente.
  useEffect(() => {
    const loadActiveServiceOrders = async () => {
      try {
        const statusesResponse = await serviceOrdersApi.getServiceOrderStatuses();
        const entregado = statusesResponse.data.data.find((status) => status.name === 'entregado');

        const totalResponse = await serviceOrdersApi.getServiceOrders({ per_page: 1 });
        const deliveredResponse = entregado
          ? await serviceOrdersApi.getServiceOrders({ per_page: 1, status: entregado.id })
          : null;

        const total = totalResponse.data.meta.total;
        const delivered = deliveredResponse ? deliveredResponse.data.meta.total : 0;
        setActiveServiceOrders(total - delivered);
      } catch (error) {
        // Metrica secundaria: si falla, se deja el placeholder sin interrumpir el dashboard.
      }
    };

    loadActiveServiceOrders();
  }, []);

  // KPIs y graficos de ventas / utilidad de componentes / utilidad y estado de ordenes de
  // servicio, agrupados segun la granularidad elegida. Todo esto es solo para administrador
  // (el costo de cada producto, base de la utilidad, es un dato sensible).
  useEffect(() => {
    if (!isAdmin) return undefined;

    let active = true;
    setStatsLoading(true);

    Promise.all([
      ordersApi.getSalesStats(granularity),
      productsApi.getProductStats(granularity),
      serviceOrdersApi.getServiceOrderStats(granularity),
    ])
      .then(([salesRes, productRes, serviceRes]) => {
        if (!active) return;
        setSalesStats(salesRes.data);
        setProductStats(productRes.data);
        setServiceStats(serviceRes.data);
      })
      .catch(() => {
        // Metricas secundarias: si fallan, se dejan los placeholders sin interrumpir el dashboard.
      })
      .finally(() => {
        if (active) setStatsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [isAdmin, granularity]);

  const metrics = useMemo(
    () => [
      { label: 'Productos activos', value: '—', icon: <InventoryIcon />, color: 'primary' },
      { label: 'Cotizaciones del mes', value: '—', icon: <RequestQuoteIcon />, color: 'info' },
      {
        label: 'Órdenes de servicio activas',
        value: activeServiceOrders ?? '—',
        icon: <BuildIcon />,
        color: 'success',
      },
    ],
    [activeServiceOrders]
  );

  const salesKpis = useMemo(() => {
    const kpis = salesStats?.kpis;
    return [
      {
        label: 'Ventas de hoy',
        value: kpis ? formatMoney(kpis.today.total) : '—',
        caption: kpis ? `${kpis.today.count} pedido(s)` : undefined,
        icon: <TodayIcon />,
        color: 'primary',
      },
      {
        label: 'Ventas de la semana',
        value: kpis ? formatMoney(kpis.week.total) : '—',
        caption: kpis ? `${kpis.week.count} pedido(s)` : undefined,
        icon: <DateRangeIcon />,
        color: 'info',
      },
      {
        label: 'Ventas del mes',
        value: kpis ? formatMoney(kpis.month.total) : '—',
        caption: kpis ? `${kpis.month.count} pedido(s)` : undefined,
        icon: <CalendarMonthIcon />,
        color: 'warning',
      },
      {
        label: 'Ventas del año',
        value: kpis ? formatMoney(kpis.year.total) : '—',
        caption: kpis ? `${kpis.year.count} pedido(s)` : undefined,
        icon: <EventRepeatIcon />,
        color: 'success',
      },
    ];
  }, [salesStats]);

  // Utilidad por componentes vendidos (ingreso - costo de cada producto) + valor de
  // inventario actual a precio de costo, como quinta tarjeta.
  const productKpis = useMemo(() => {
    const kpis = productStats?.kpis;
    const periods = [
      { key: 'today', label: 'Utilidad de hoy', icon: <TodayIcon />, color: 'primary' },
      { key: 'week', label: 'Utilidad de la semana', icon: <DateRangeIcon />, color: 'info' },
      { key: 'month', label: 'Utilidad del mes', icon: <CalendarMonthIcon />, color: 'warning' },
      { key: 'year', label: 'Utilidad del año', icon: <EventRepeatIcon />, color: 'success' },
    ].map(({ key, label, icon, color }) => ({
      label,
      value: kpis ? formatMoney(kpis[key].profit) : '—',
      caption: kpis ? `Ingreso ${formatMoney(kpis[key].revenue)} · Costo ${formatMoney(kpis[key].cost)}` : undefined,
      icon,
      color,
    }));

    periods.push({
      label: 'Valor de inventario',
      value: productStats ? formatMoney(productStats.inventory_value) : '—',
      caption: 'Stock disponible a precio de costo',
      icon: <WarehouseIcon />,
      color: 'secondary',
    });

    return periods;
  }, [productStats]);

  // Utilidad por ordenes de servicio (precio del servicio, ver ServiceOrderStatsController).
  const serviceProfitKpis = useMemo(() => {
    const kpis = serviceStats?.kpis;
    return [
      { key: 'today', label: 'Utilidad de hoy', icon: <TodayIcon />, color: 'primary' },
      { key: 'week', label: 'Utilidad de la semana', icon: <DateRangeIcon />, color: 'info' },
      { key: 'month', label: 'Utilidad del mes', icon: <CalendarMonthIcon />, color: 'warning' },
      { key: 'year', label: 'Utilidad del año', icon: <EventRepeatIcon />, color: 'success' },
    ].map(({ key, label, icon, color }) => ({
      label,
      value: kpis ? formatMoney(kpis[key].revenue) : '—',
      caption: kpis ? `${kpis[key].count} orden(es)` : undefined,
      icon,
      color,
    }));
  }, [serviceStats]);

  // Ordenes de servicio pendientes vs terminadas por periodo.
  const serviceStatusKpis = useMemo(() => {
    const kpis = serviceStats?.status_kpis;
    return [
      { key: 'today', label: 'Hoy', icon: <TodayIcon />, color: 'primary' },
      { key: 'week', label: 'Esta semana', icon: <DateRangeIcon />, color: 'info' },
      { key: 'month', label: 'Este mes', icon: <CalendarMonthIcon />, color: 'warning' },
      { key: 'year', label: 'Este año', icon: <EventRepeatIcon />, color: 'success' },
    ].map(({ key, label, icon, color }) => ({
      label,
      value: kpis ? `${kpis[key].pendiente} / ${kpis[key].terminada}` : '—',
      caption: 'Pendientes / Terminadas',
      icon,
      color,
    }));
  }, [serviceStats]);

  return (
    <Stack spacing={3}>
      <Stack sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
      >
        <div>
          <Typography variant="h4">Hola, {user?.name || 'bienvenido'} 👋</Typography>
          <Typography variant="body2" color="text.secondary">
            Resumen general de la operación de PalaciComputers.
          </Typography>
        </div>

        {isAdmin && (
          <ToggleButtonGroup
            size="small"
            color="primary"
            exclusive
            value={granularity}
            onChange={(event, value) => value && setGranularity(value)}
          >
            {GRANULARITY_OPTIONS.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Stack>

      <Grid container spacing={3}>
        {metrics.map((metric) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={metric.label}>
            <MetricCard {...metric} />
          </Grid>
        ))}
      </Grid>

      {isAdmin && (
        <>
          {/* Ventas */}
          <Grid container spacing={3}>
            {salesKpis.map((metric) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={metric.label}>
                <MetricCard {...metric} />
              </Grid>
            ))}
          </Grid>

          {/* Utilidad por componentes vendidos + valor de inventario */}
          <Grid container spacing={3}>
            {productKpis.map((metric) => (
              <Grid size={{ xs: 12, sm: 6, md: 2.4 }} key={metric.label}>
                <MetricCard {...metric} />
              </Grid>
            ))}
          </Grid>

          <ChartCard
            title="Utilidad por componentes vendidos"
            description="Ingreso menos costo de cada producto en pedidos pagados,"
            granularityLabel={granularityLabel}
            empty={!statsLoading && !productStats?.series?.data?.length}
          >
            <AreaChart data={productStats?.series?.data || []} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="colorUtilidad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00A76F" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#00A76F" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(value, name) => [
                  formatMoney(value),
                  { profit: 'Utilidad', revenue: 'Ingreso', cost: 'Costo' }[name] || name,
                ]}
              />
              <Area type="monotone" dataKey="profit" stroke="#00A76F" fill="url(#colorUtilidad)" strokeWidth={2} />
            </AreaChart>
          </ChartCard>

          {/* Utilidad por ordenes de servicio */}
          <Grid container spacing={3}>
            {serviceProfitKpis.map((metric) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={metric.label}>
                <MetricCard {...metric} />
              </Grid>
            ))}
          </Grid>

          <ChartCard
            title="Utilidad por órdenes de servicio"
            description="Suma del precio de cada orden de servicio recibida,"
            granularityLabel={granularityLabel}
            empty={!statsLoading && !serviceStats?.series?.data?.length}
          >
            <AreaChart data={serviceStats?.series?.data || []} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="colorServicio" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FFAB00" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#FFAB00" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip
                formatter={(value, name) => [name === 'revenue' ? formatMoney(value) : value, name === 'revenue' ? 'Utilidad' : 'Órdenes']}
              />
              <Area type="monotone" dataKey="revenue" stroke="#FFAB00" fill="url(#colorServicio)" strokeWidth={2} />
            </AreaChart>
          </ChartCard>

          {/* Ordenes de servicio: pendientes vs terminadas */}
          <Grid container spacing={3}>
            {serviceStatusKpis.map((metric) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={metric.label}>
                <MetricCard {...metric} />
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Stack>
  );
}
